import React, { useState } from 'react';
import { Trash2, Edit, ArrowDownWideNarrow, ArrowDownNarrowWide, ArrowUpDown } from 'lucide-react';

const ExpensesTab = ({ expenses, setExpenses, savedExpenses, setSavedExpenses, expenseOptions }) => {
  const [expenseSearch, setExpenseSearch] = useState('');
  const [editingExpenseIdx, setEditingExpenseIdx] = useState(null);
  const [expenseSort, setExpenseSort] = useState({ column: 'memo', direction: 'asc' });
  const [expenseForm, setExpenseForm] = useState({ expense: '', date: '', memo: '', amount: '', percent: '100' });

  // An expense row stores both `amount` and `percent`. The effective amount
  // (what the project actually owes) is `amount × percent / 100`. The
  // saved-expenses table and Total row both display this effective value
  // so the UI agrees with the backend's expense_box math.
  const effectiveAmount = (exp) => {
    const amt = parseFloat(String(exp.amount || '0').replace(/[$,]/g, '')) || 0;
    const pctRaw = parseFloat(exp.percent);
    const pct = Number.isNaN(pctRaw) || pctRaw <= 0 ? 100 : pctRaw;
    return amt * pct / 100;
  };

  const handleAddExpense = () => {
    setExpenses(prev => [...prev, { expense: '', date: '', memo: '', amount: '', percent: '100' }]);
  };

  const handleDeleteExpense = (index) => {
    setExpenses(prev => prev.filter((_, i) => i !== index));
  };

  const updateExpense = (index, field, value) => {
    setExpenses(prev => prev.map((exp, i) => i === index ? { ...exp, [field]: value } : exp));
  };

  const handleEditSavedExpense = (index) => {
    const exp = savedExpenses[index];
    setExpenseForm({ memo: exp.memo || '', amount: String(exp.amount || ''), percent: String(exp.percent ?? '100') });
    setEditingExpenseIdx(index);
  };

  const handleDeleteSavedExpense = (index) => {
    setSavedExpenses(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateExpense = () => {
    if (editingExpenseIdx === null) return;
    const now = new Date();
    const dateStr = `${now.getMonth()+1}/${now.getDate()}/${now.getFullYear()} ${now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase()}`;
    const updatedBy = (() => { try { const u = JSON.parse(localStorage.getItem('user')); return u?.name || ''; } catch { return ''; } })();
    setSavedExpenses(prev => prev.map((e, i) => i === editingExpenseIdx ? {
      ...e, memo: expenseForm.memo,
      amount: parseFloat(String(expenseForm.amount || '0').replace(/[$,]/g, '')) || 0,
      percent: parseFloat(String(expenseForm.percent || '100').replace(/[%,]/g, '')) || 100,
      last_updated: dateStr,
      last_updated_by: updatedBy,
    } : e));
    setEditingExpenseIdx(null);
    setExpenseForm({ expense: '', date: '', memo: '', amount: '', percent: '100' });
  };

  return (
    <div className="bg-white rounded shadow p-6">
      <h2 className="text-xl text-[#5B8DB8] mb-6">Project Expenses</h2>

      {/* Column Headers */}
      <div className="grid grid-cols-[180px_140px_1fr_100px_80px_auto] gap-4 mb-2">
        <div className="text-sm text-gray-600">Expense</div>
        <div className="text-sm text-gray-600">Date</div>
        <div className="text-sm text-gray-600">Memo</div>
        <div className="text-sm text-gray-600">Amount</div>
        <div className="text-sm text-gray-600">Percent</div>
        <div className="w-8"></div>
      </div>

      {/* Editable Expense Rows */}
      <div className="border-t">
        {expenses.map((exp, idx) => (
          <div key={idx} className={`grid grid-cols-[180px_140px_1fr_100px_80px_auto] gap-4 py-3 ${idx > 0 ? 'border-t' : ''} ${idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`} data-testid={`expense-row-${idx}`}>
            <select
              value={exp.expense}
              onChange={(e) => updateExpense(idx, 'expense', e.target.value)}
              className="border border-gray-300 rounded px-3 py-2"
              data-testid={`expense-select-${idx}`}
            >
              <option value="">-- select --</option>
              {expenseOptions.map(o => (
                <option key={o.name} value={o.name}>{o.name}</option>
              ))}
            </select>
            <input
              type="date"
              value={exp.date}
              onChange={(e) => updateExpense(idx, 'date', e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 w-full"
              data-testid={`expense-date-${idx}`}
            />
            <input
              type="text"
              value={exp.memo}
              onChange={(e) => updateExpense(idx, 'memo', e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 w-full"
              data-testid={`expense-memo-${idx}`}
            />
            <input
              type="text"
              value={exp.amount || ''}
              onChange={(e) => updateExpense(idx, 'amount', e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 w-24"
              data-testid={`expense-amount-${idx}`}
            />
            <input
              type="text"
              value={exp.percent}
              onChange={(e) => updateExpense(idx, 'percent', e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 w-20"
              data-testid={`expense-percent-${idx}`}
            />
            <div className="w-8 flex items-center justify-center">
              {idx > 0 && (
                <button
                  onClick={() => handleDeleteExpense(idx)}
                  className="bg-[#DC3545] hover:bg-[#C82333] text-white rounded p-1.5"
                  data-testid={`delete-expense-${idx}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="border-t mt-2 pt-4">
        <button
          onClick={handleAddExpense}
          className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-4 py-2 rounded transition-colors"
          data-testid="add-expense-btn"
        >
          Add Expense
        </button>
      </div>

      {/* Saved Expenses Table */}
      <div className="mt-8">
        <div className="flex justify-end mb-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Search:</span>
            <input
              type="text"
              value={expenseSearch}
              onChange={(e) => setExpenseSearch(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 w-48"
              data-testid="expense-search"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2">
                <th className="text-left p-2 text-gray-600 font-normal">
                  <button onClick={() => setExpenseSort(s => s.column === 'expense' ? { column: 'expense', direction: s.direction === 'asc' ? 'desc' : 'asc' } : { column: 'expense', direction: 'asc' })} className="flex items-center gap-1 hover:text-gray-800" data-testid="sort-expense">
                    {expenseSort.column === 'expense'
                      ? (expenseSort.direction === 'asc' ? <ArrowDownNarrowWide className="w-4 h-4 text-red-500" /> : <ArrowDownWideNarrow className="w-4 h-4 text-red-500" />)
                      : <ArrowUpDown className="w-4 h-4 text-gray-400" />}
                    <span className={expenseSort.column === 'expense' ? 'text-red-500' : ''}>Expense</span>
                  </button>
                </th>
                <th className="text-left p-2 text-gray-600 font-normal">
                  <button onClick={() => setExpenseSort(s => s.column === 'date' ? { column: 'date', direction: s.direction === 'asc' ? 'desc' : 'asc' } : { column: 'date', direction: 'asc' })} className="flex items-center gap-1 hover:text-gray-800" data-testid="sort-date">
                    {expenseSort.column === 'date'
                      ? (expenseSort.direction === 'asc' ? <ArrowDownNarrowWide className="w-4 h-4 text-red-500" /> : <ArrowDownWideNarrow className="w-4 h-4 text-red-500" />)
                      : <ArrowUpDown className="w-4 h-4 text-gray-400" />}
                    <span className={expenseSort.column === 'date' ? 'text-red-500' : ''}>Date</span>
                  </button>
                </th>
                <th className="text-left p-2 font-normal">
                  <button onClick={() => setExpenseSort(s => s.column === 'memo' ? { column: 'memo', direction: s.direction === 'asc' ? 'desc' : 'asc' } : { column: 'memo', direction: 'asc' })} className="flex items-center gap-1 hover:text-gray-800" data-testid="sort-memo">
                    {expenseSort.column === 'memo'
                      ? (expenseSort.direction === 'asc' ? <ArrowDownNarrowWide className="w-4 h-4 text-red-500" /> : <ArrowDownWideNarrow className="w-4 h-4 text-red-500" />)
                      : <ArrowUpDown className="w-4 h-4 text-gray-400" />}
                    <span className={expenseSort.column === 'memo' ? 'text-red-500' : 'text-gray-600'}>Memo</span>
                  </button>
                </th>
                <th className="text-left p-2 text-gray-600 font-normal">
                  <button onClick={() => setExpenseSort(s => s.column === 'amount' ? { column: 'amount', direction: s.direction === 'asc' ? 'desc' : 'asc' } : { column: 'amount', direction: 'asc' })} className="flex items-center gap-1 hover:text-gray-800" data-testid="sort-amount">
                    {expenseSort.column === 'amount'
                      ? (expenseSort.direction === 'asc' ? <ArrowDownNarrowWide className="w-4 h-4 text-red-500" /> : <ArrowDownWideNarrow className="w-4 h-4 text-red-500" />)
                      : <ArrowUpDown className="w-4 h-4 text-gray-400" />}
                    <span className={expenseSort.column === 'amount' ? 'text-red-500' : ''}>Amount</span>
                  </button>
                </th>
                <th className="text-left p-2 text-gray-600 font-normal">
                  <button onClick={() => setExpenseSort(s => s.column === 'last_updated' ? { column: 'last_updated', direction: s.direction === 'asc' ? 'desc' : 'asc' } : { column: 'last_updated', direction: 'asc' })} className="flex items-center gap-1 hover:text-gray-800" data-testid="sort-last-updated">
                    {expenseSort.column === 'last_updated'
                      ? (expenseSort.direction === 'asc' ? <ArrowDownNarrowWide className="w-4 h-4 text-red-500" /> : <ArrowDownWideNarrow className="w-4 h-4 text-red-500" />)
                      : <ArrowUpDown className="w-4 h-4 text-gray-400" />}
                    <span className={expenseSort.column === 'last_updated' ? 'text-red-500' : ''}>Last Updated On</span>
                  </button>
                </th>
                <th className="text-left p-2 text-gray-600 font-normal"></th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const saved = savedExpenses.filter(exp => {
                  if (!expenseSearch.trim()) return true;
                  const term = expenseSearch.toLowerCase();
                  return [exp.expense, exp.date, exp.memo, String(exp.amount), exp.last_updated]
                    .some(f => (f || '').toLowerCase().includes(term));
                });
                const sorted = [...saved].sort((a, b) => {
                  const col = expenseSort.column;
                  const dir = expenseSort.direction === 'asc' ? 1 : -1;
                  if (col === 'amount') {
                    return (effectiveAmount(a) - effectiveAmount(b)) * dir;
                  }
                  const aVal = (a[col] || '').toLowerCase();
                  const bVal = (b[col] || '').toLowerCase();
                  return aVal < bVal ? -dir : aVal > bVal ? dir : 0;
                });
                if (sorted.length === 0) return (
                  <tr>
                    <td colSpan="6" className="p-4 text-center text-gray-500 italic">No data available in table</td>
                  </tr>
                );
                return sorted.map((exp, idx) => {
                  // The edit/delete handlers operate on the original savedExpenses array,
                  // but `idx` here is the index AFTER sorting/filtering. Resolve the
                  // original index via object identity so we mutate the right row.
                  const realIdx = savedExpenses.indexOf(exp);
                  return (
                  <tr key={realIdx} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} data-testid={`saved-expense-row-${idx}`}>
                    <td className="p-2 text-gray-700">{exp.expense || '--'}</td>
                    <td className="p-2 text-gray-700">{exp.date}</td>
                    <td className="p-2 text-gray-700">{exp.memo}</td>
                    <td className="p-2 text-gray-700" data-testid={`saved-expense-amount-${idx}`}>
                      ${effectiveAmount(exp).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {(() => {
                        const pct = parseFloat(exp.percent);
                        if (!Number.isNaN(pct) && pct > 0 && pct !== 100) {
                          const raw = parseFloat(String(exp.amount || '0').replace(/[$,]/g, '')) || 0;
                          return <div className="text-xs text-gray-500">${raw.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} × {pct}%</div>;
                        }
                        return null;
                      })()}
                    </td>
                    <td className="p-2 text-gray-700 text-xs">
                      <div>{exp.last_updated}</div>
                      {exp.last_updated_by && <div className="text-gray-500">by {exp.last_updated_by}</div>}
                    </td>
                    <td className="p-2">
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleEditSavedExpense(realIdx)} className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white rounded p-1" data-testid={`edit-saved-expense-${idx}`}>
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeleteSavedExpense(realIdx)} className="bg-[#DC3545] hover:bg-[#C82333] text-white rounded p-1" data-testid={`delete-saved-expense-${idx}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                });
              })()}
            </tbody>
            {savedExpenses.length > 0 && (
              <tfoot>
                <tr className="border-t-2 font-medium">
                  <td className="p-2" colSpan="3">
                    <span className="text-sm text-gray-600">Total Expenses</span>
                  </td>
                  <td className="p-2 text-sm text-gray-700">
                    ${savedExpenses.reduce((sum, e) => sum + effectiveAmount(e), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="p-2"></td>
                  <td className="p-2"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <div className="mt-4 text-sm text-gray-500">
          Showing {savedExpenses.length > 0 ? 1 : 0} to {savedExpenses.length} of {savedExpenses.length} entries
        </div>
      </div>

      {/* Edit Expense Modal */}
      {editingExpenseIdx !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-16 z-50" onMouseDown={(e) => { if (e.target === e.currentTarget) { setEditingExpenseIdx(null); setExpenseForm({ expense: '', date: '', memo: '', amount: '', percent: '100' }); } }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-testid="edit-expense-modal">
            <h2 className="text-2xl text-[#5B8DB8] font-light mb-1">Edit Expense</h2>
            <p className="text-sm text-gray-500 mb-6">Please update the expense details:</p>
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 block mb-1">Memo note</label>
              <input type="text" value={expenseForm.memo} onChange={(e) => setExpenseForm({ ...expenseForm, memo: e.target.value })} className="border border-gray-300 rounded px-3 py-2 w-full" data-testid="edit-expense-memo" />
            </div>
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 block mb-1">Amount</label>
              <input type="text" value={expenseForm.amount} onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} className="border border-gray-300 rounded px-3 py-2 w-full" data-testid="edit-expense-amount" />
            </div>
            <div className="mb-6">
              <label className="text-sm font-medium text-gray-700 block mb-1">Percent</label>
              <input type="text" value={expenseForm.percent} onChange={(e) => setExpenseForm({ ...expenseForm, percent: e.target.value })} className="border border-gray-300 rounded px-3 py-2 w-full" data-testid="edit-expense-percent" />
              <div className="text-xs text-gray-500 mt-1">
                Effective: ${(((parseFloat(expenseForm.amount) || 0) * (parseFloat(expenseForm.percent) || 100)) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setEditingExpenseIdx(null); setExpenseForm({ expense: '', date: '', memo: '', amount: '', percent: '100' }); }} className="bg-[#6C7B8A] hover:bg-[#5A6878] text-white px-5 py-2 rounded text-sm font-medium transition-colors" data-testid="edit-expense-cancel">Cancel</button>
              <button onClick={handleUpdateExpense} className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-5 py-2 rounded text-sm font-medium transition-colors" data-testid="edit-expense-update">Update</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesTab;
